<script lang="ts">
import { navigateTo, useRoute } from '#app/composables/router';
import { useItemListJsonLd } from '~/composables/useItemListJsonLd';
import { computed, ref } from 'vue';
import { definePageMeta } from '#imports';
import type { RouteLocationNormalized } from 'vue-router';

export function middleware(_to: RouteLocationNormalized) {
  const { setBreadcrumbs } = useBreadcrumbs();
  setBreadcrumbs([
    {
      to: '/public/article/recipe/top',
      text: 'ダイエットレシピ',
    },
    {
      text: '献立',
    },
  ]);
}
</script>

<script setup lang="ts">
import SearchIcon from 'vue-material-design-icons/Magnify.vue';
import { normalizeNumberQueryParameter } from '@shared/app/utils/queryNormalizers';
import sanitizeHtml from 'sanitize-html';
import { useSeoMeta } from '#app/composables/head';
import { useFetch } from '#app/composables/fetch';
import { useBreadcrumbs } from '~/composables/useBreadcrumbs';

definePageMeta({
  layout: 'recipe',
  middleware,
});

// NOTE: 検索キーワードやソート順は保持し、他はリセット
function createQueryToReset(option: { tg?: number | undefined }) {
  return {
    page: 1,
    s: route.query.s,
    order: route.query.order,
    tg: option.tg,
  };
}

const route = useRoute();

const searchKeyword = ref(route.query.s as string);
const currentSearchKeyword = computed(() => {
  return route.query.s as string;
});

const currentPage = computed(() => {
  return normalizeNumberQueryParameter(route.query.page as string, 1);
});

const currentTagId = computed(() => {
  const tg = route.query.tg as string | undefined;
  return tg ? Number(tg) : undefined;
});

const currentOrder = computed(() => {
  return route.query.order as string;
});

const currentOrderTitle = computed(() => {
  return (
    orderItems.find((o) => o.value === currentOrder.value)?.title ?? '新着順'
  );
});

const limit = 20;

const { data: menus } = await useFetch('/api/kiwi_user/menus', {
  query: {
    page: computed(() => currentPage.value),
    limit,
    tg: computed(() => currentTagId.value),
    s: computed(() => currentSearchKeyword.value),
    order: computed(() => currentOrder.value),
  },
});

const { data: menuTags } = await useFetch(
  '/api/kiwi_user/recipe_tags/for_menu',
);

const flattenTags = computed(() => {
  return menuTags.value?.flatMap((group) => group.recipe_tags) ?? [];
});

const currentTagName = computed(
  () => flattenTags.value.find((t) => t.id === currentTagId.value)?.name,
);

const currentTagDescription = computed(() => {
  return flattenTags.value.find((t) => t.id === currentTagId.value)
    ?.description;
});

const pageTitle = computed(() => {
  const DEFAULT_TITLE = 'すべての献立';

  const tagText = currentTagName.value;
  const keywordText = currentSearchKeyword.value
    ? `「${currentSearchKeyword.value}」`
    : '';

  if (!tagText && !keywordText) return DEFAULT_TITLE;

  const joinedText = [tagText, keywordText].filter(Boolean).join('、');
  return `${joinedText}献立`;
});

const orderItems = [
  {
    title: '新着順',
    value: 'new',
  },
  {
    title: '人気順',
    value: 'rank',
  },
];

useSeoMeta({
  title: computed(() => `${pageTitle.value}（${currentOrderTitle.value}）`),
});

useItemListJsonLd(
  computed(() => menus.value?.menus),
  '/public/article/menu/',
);

function isActiveOrder(value: string) {
  if (value === 'new') {
    return currentOrder.value === 'new' || !currentOrder.value;
  }

  if (value === 'rank') {
    return currentOrder.value === 'rank';
  }

  return false;
}

async function setOrder(value: string) {
  await navigateTo({
    query: {
      ...route.query,
      page: 1,
      order: value,
    },
  });
}

async function submitKeywordSearch() {
  await navigateTo({
    query: {
      ...route.query,
      page: 1,
      s:
        sanitizeHtml(searchKeyword.value, {
          allowedTags: [],
          allowedAttributes: {},
        }) ?? undefined,
    },
  });
}

async function changePage(page: number) {
  await navigateTo({
    query: {
      ...route.query,
      page,
    },
  });
}

const totalCount = computed(() => {
  return (menus.value?.meta.totalCount ?? 0).toLocaleString();
});

async function changeTag(tagId?: number) {
  await navigateTo({
    query: createQueryToReset({ tg: tagId }),
  });
}
</script>

<template>
  <div>
    <div
      class="md:flex border-cream-500 border border-b-2 rounded py-3 bg-white"
    >
      <OrganismsMenuListNavigationPc
        v-if="menuTags?.length"
        class="max-md:hidden w-[28%] shrink-0"
        :menu-tags="menuTags"
        @change-tag="changeTag"
      />

      <div class="grow p-3">
        <h1 class="text-gray-500">
          <span class="font-bold text-2xl">{{ pageTitle }}</span>
          <span data-testid="total-count">（{{ totalCount }}件）</span>
        </h1>

        <p v-if="currentTagDescription" class="text-gray-400 mt-1 text-sm">
          {{ currentTagDescription }}
        </p>

        <div class="md:flex justify-between border-cream-500 border-b my-3">
          <form
            class="relative md:max-w-1/2 pb-1 max-md:mb-3"
            @submit.prevent="submitKeywordSearch"
          >
            <SearchIcon
              class="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400"
              :size="20"
            />

            <input
              v-model="searchKeyword"
              placeholder="料理名、食材、目的"
              class="border border-gray-300 rounded-md pl-10 pr-3 py-1 max-md:py-2 w-full shadow-inner focus:bg-[#f6f3f2]"
            />
          </form>

          <OrganismsMenuListNavigationSp
            v-if="menuTags?.length"
            class="md:hidden mb-5"
            :menu-tags="menuTags"
            :current-tag-id="currentTagId"
            @change-tag="changeTag"
          />

          <div class="shrink-0 flex gap-2">
            <button
              v-for="order in orderItems"
              class="max-md:py-3 flex items-center font-bold text-gray-500 leading-none px-5 border-x border-t-2 border-cream-500 rounded-t-md"
              :class="{
                'border-t-pink-400': isActiveOrder(order.value),
              }"
              @click="setOrder(order.value)"
            >
              {{ order.title }}
            </button>
          </div>
        </div>

        <ul class="grid grid-cols-2 max-md:grid-cols-1 gap-3">
          <li v-for="(menu, i) in menus?.menus" :key="menu.id">
            <AtomsMenuCard
              :old-identifier="menu.old_identifier"
              :menu-id="menu.id"
              :title="menu.title"
              :main-image="menu.main_image"
              :energy="menu.energy"
              :recipe-count="menu.recipes.length"
              :tags="menu.tags"
              :recipes="menu.recipes"
              :rank="
                currentOrder === 'rank' && currentPage === 1 ? i + 1 : undefined
              "
            />
          </li>
        </ul>

        <div
          v-if="(menus?.meta.totalPages ?? 0) > 1"
          class="p-5 border border-b-2 border-cream-500 rounded text-center text-gray-500 mt-5"
        >
          <AtomsRecipePagination
            class="mb-4"
            :total-pages="menus?.meta.totalPages ?? 0"
            :current-page="currentPage"
            @change-page="changePage"
          />

          <AtomsRecipePaginationCount
            :total-count="menus?.meta.totalCount ?? 0"
            :current-page="currentPage"
            :limit="limit"
          />
        </div>

        <AtomsSearchResultCount v-else :total-count="totalCount" />
      </div>
    </div>

    <OrganismsMenuHistory class="my-10" />

    <OrganismsRecipeBottomAdsPc />
  </div>
</template>
